import { getPOIColorCssValue, getPOISymbol, type GeolocationPosition, calculateDistance } from '@inloco/shared';
import type { TranslatedPOI } from '@/types/poi';
import { useTranslation } from 'react-i18next';
import Dialog from '../ui/Dialog';
import {
    ContactActionWidget,
    GenericMetaGridWidget,
    LiveOccupancyWidget,
    EmergencyWidget,
    WaitingTimeWidget,
    CleaningWidget,
    OccupationStatusWidget,
    ElectricChargingWidget
} from '../ui/widget';
import { Icon } from '../ui/Icon';

interface POIModalProps {
    poi: TranslatedPOI | null;
    isOpen: boolean;
    position: GeolocationPosition | null;
    onClose: () => void;
}

const POIModal = ({ poi, isOpen, position, onClose }: POIModalProps) => {
    const { t } = useTranslation();

    if (!poi) return null;

    // Helper to render metadata based on POI type
    // Questa funzione incapsula tutta la logica di rendering dei meta, con template specifici per tipo e un fallback generico.
    const renderMetadata = () => {
        if (!poi.meta) return null;

        const meta = poi.meta; // Usiamo le chiavi per guidarci

        if (!meta) return null;

        // Capability: Live
        const currentOccupancy = 'currentOccupancy' in meta ? meta.currentOccupancy : undefined;
        const capacity = 'capacity' in meta ? meta.capacity : undefined;
        const temperature = 'temperature' in meta ? meta.temperature : undefined;
        const updatedAt = 'updatedAt' in meta ? meta.updatedAt : undefined;

        // Capability: Contact
        const phoneNumber = 'phoneNumber' in meta ? meta.phoneNumber : undefined;
        const bookingAction = 'bookingAction' in meta ? meta.bookingAction : undefined;
        const openingHours = 'openingHours' in meta ? meta.openingHours : undefined;

        // Capability: Safety
        const emergencyLevel = 'emergencyLevel' in meta ? meta.emergencyLevel : undefined;

        // Capability: Waiting Time
        const waitingTime = 'waitingTime' in meta ? meta.waitingTime : undefined;

        // Capability: IsOperational
        const isOperational = 'isOperational' in meta ? meta.isOperational : undefined;

        // Capability: IsOccupied (es. per aree picnic)
        const isOccupied = 'isOccupied' in meta ? meta.isOccupied : undefined;

        // Capability: HasElectricCharging (es. per colonnine)
        const hasElectricCharging = 'hasElectricCharging' in meta ? meta.hasElectricCharging : undefined;

        // Resto meta per griglia generica
        const remainingMeta: Record<string, unknown> = { ...meta };
        delete remainingMeta.currentOccupancy;
        delete remainingMeta.capacity;
        delete remainingMeta.temperature;
        delete remainingMeta.updatedAt;
        delete remainingMeta.phoneNumber;
        delete remainingMeta.bookingAction;
        delete remainingMeta.openingHours;
        delete remainingMeta.emergencyLevel;
        delete remainingMeta.waitingTime;
        delete remainingMeta.isOperational;
        delete remainingMeta.isOccupied;
        delete remainingMeta.hasElectricCharging;

        // Identifichiamo le "Capabilities" presenti nel payload
        const hasOccupancyData = currentOccupancy || capacity || temperature;
        const hasContactData = phoneNumber || bookingAction || openingHours;
        const isEmergency = emergencyLevel === 'High';

        return (
            <div className="flex flex-col gap-6">
                {/* Priorità Massima: Alert di Sicurezza */}
                {isEmergency && (
                    <EmergencyWidget level={emergencyLevel} t={t} />
                )}

                {/* Widget per ricarica elettrica, se presente */}
                {hasElectricCharging && (
                    <ElectricChargingWidget
                        hasElectricCharging={hasElectricCharging}
                        t={t}
                    />
                )}

                {/* Widget Dinamico: Se ha dati da sensori/capacità, usa il LiveOccupancy */}
                {hasOccupancyData && (
                    <LiveOccupancyWidget
                        current={currentOccupancy}
                        total={capacity}
                        temperature={temperature}
                        updatedAt={updatedAt}
                        t={t}
                    />
                )}

                {/* Sezione Contatti/Operatività: Se ha orari o numeri di telefono */}
                {hasContactData && (
                    <ContactActionWidget
                        openingHours={openingHours}
                        phoneNumber={phoneNumber}
                        bookingAction={bookingAction}
                        t={t}
                    />
                )}

                {/* Widget specifico per tempi di attesa, se presente */}
                {waitingTime && (
                    <WaitingTimeWidget
                        waitingTime={waitingTime}
                        t={t}
                    />
                )}

                {/* Widget is operational per docce, bagni e punti acqua */}
                {isOperational !== undefined && (
                    <CleaningWidget
                        updatedAt={updatedAt}
                        isOperational={isOperational}
                        t={t}
                    />
                )}

                {/* Widget occupazione per aree picnic o sedute, se è presente isOccupied */}
                {isOccupied !== undefined && (
                    <OccupationStatusWidget
                        isOccupied={isOccupied}
                        t={t}
                    />
                )}

                {/* Tutto il resto */}
                <GenericMetaGridWidget
                    meta={remainingMeta}
                    t={t}
                />
            </div>
        );
    };

    const renderDistance = () => {
        if (!position || !poi.position || !isOpen) return null;

        const distance = calculateDistance(position.lt, position.lg, poi.position.lt, poi.position.lg);
        return (
            <div className="flex items-center gap-2 text-sm opacity-70">
                <Icon name="distance" className="text-2xl leading-none" />
                <span>{t('poiModal.distance', { distance })}</span>
            </div>
        );
    };

    return (
        <Dialog isOpen={isOpen} onClose={onClose}>
            {/* --- HEADER --- */}
            <div className="relative bg-linear-to-br from-primary/10 to-secondary/10 p-6 sm:rounded-t-2xl rounded-t-3xl border-b border-base-300 shrink-0">
                {/* Background based on category */}
                <div
                    className="absolute inset-0 opacity-10"
                    style={{ backgroundColor: getPOIColorCssValue(poi.type) }}
                ></div>

                {/* <button
                    onClick={onClose}
                    className="absolute top-2 right-2 btn btn-circle btn-ghost btn-sm z-50 text-base-content/50 hover:text-base-content"
                    aria-label="Close"
                >
                    <Icon name="close" className="text-2xl leading-none" />
                </button> */}

                <div className="relative z-10 flex flex-col gap-4">
                    {/* Badge + Distance */}
                    <div className="flex justify-between items-center">
                        <span className="badge badge-outline badge-xs uppercase font-black tracking-widest opacity-60 px-2 py-2.5">
                            {poi.type.replace(/_/g, ' ')}
                        </span>
                        <div className="shrink-0">
                            {renderDistance()}
                        </div>
                    </div>

                    {/* Row with icon and title */}
                    <div className="flex items-center gap-4">
                        <div className="bg-base-100 shadow-md rounded-2xl p-2.5 flex items-center justify-center shrink-0 border border-base-200">
                            <Icon
                                name={getPOISymbol(poi.type)}
                                className="text-3xl leading-none"
                                style={{ color: getPOIColorCssValue(poi.type) }}
                            />
                        </div>

                        <h2 className="text-2xl font-black leading-tight wrap-break-word pr-4">
                            {poi.name}
                        </h2>
                    </div>
                </div>
            </div>

            {/* --- BODY --- */}
            <div className="flex-1 overflow-y-auto p-6 space-y-8">

                {/* Description Section: Without "h3" title (cleaner) */}
                {poi.description && (
                    <section>
                        <p className="text-base text-base-content/80 leading-relaxed font-medium italic border-l-4 border-primary/20 pl-4">
                            "{poi.description}"
                        </p>
                    </section>
                )}

                {/* Render Meta Widgets (Occupancy, Info, etc.) */}
                {/* We place them centrally because they are the added value */}
                <section className="bg-base-200/20 rounded-3xl">
                    {renderMetadata()}
                </section>

                {/* Details Section (Equipment/Services): Compact Grid */}
                {poi.details && poi.details.length > 0 && (
                    <section className="space-y-3">
                        <h3 className="text-[10px] font-black uppercase tracking-[0.2em] opacity-40 ml-1">
                            {t('poiModal.details')}
                        </h3>
                        <div className="grid grid-cols-1 gap-2">
                            {poi.details.map((d) => (
                                <div key={d.id} className="bg-base-200/40 border border-base-300/10 rounded-2xl p-4">
                                    <div className="text-[10px] font-black uppercase opacity-40 mb-1">
                                        {d.label}
                                    </div>
                                    <div className="text-sm font-semibold">{d.text}</div>
                                </div>
                            ))}
                        </div>
                    </section>
                )}
            </div>

        </Dialog>
    );
};



export default POIModal;
